// Graph building and setup utilities
import Graph from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import { calculateHierarchicalPositions } from './nodePositioning';
import { getNodeStyle, getEdgeStyle, getEdgeWeight } from './nodeStyles';

/**
 * Build a graph instance from node and edge data
 * @param {Object} graphData - Object containing nodes and edges arrays
 * @returns {Object} Graphology graph instance
 */
export const buildGraph = (graphData) => {
  const graph = new Graph();
  
  // Calculate node positions
  const nodePositions = calculateHierarchicalPositions(graphData.nodes);
  
  // Add nodes with styling and positioning
  graphData.nodes.forEach(node => {
    const { size, color, labelSize } = getNodeStyle(node);
    const position = nodePositions[node.id] || { x: 0, y: 0 };
    
    graph.addNode(node.id, {
      label: node.label || node.id, // Ensure label is never empty
      size: size,
      color: color,
      labelColor: '#000', // Black text
      labelSize: labelSize,
      borderColor: '#fff',
      borderSize: 2, // White border around nodes
      // Deterministic hierarchical positions
      x: position.x,
      y: position.y,
      // Store additional data
      content: node.content,
      visibility: node.visibility,
      timestamp: node.timestamp,
      category: node.category,
      level: node.level,
      isHub: node.isHub,
      isCluster: node.isCluster,
      isTopicArea: node.isTopicArea,
      topic: node.topic,
      subtopic: node.subtopic,
      topicArea: node.topicArea,
      highlighted: false
    });
  });

  // Add edges with styling and weights
  graphData.edges.forEach(edge => {
    const edgeStyle = getEdgeStyle(edge);
    graph.addEdge(edge.source, edge.target, {
      size: edgeStyle.size,
      color: edgeStyle.color,
      type: edgeStyle.type,
      weight: edge.weight || getEdgeWeight(edge.type)
    });
  });

  return graph;
};

// Store FA2 layout instance globally for cleanup
let fa2LayoutInstance = null;

/**
 * Apply Force Atlas 2 layout to a graph
 * @param {Object} graph - Graphology graph instance
 * @param {boolean} force - Whether to force layout application
 * @returns {Promise<Object>} Promise resolving to graph with applied layout
 */
export const applyLayout = async (graph, force = false) => {
  try {
    // Clean up any existing layout
    if (fa2LayoutInstance) {
      fa2LayoutInstance.kill();
      fa2LayoutInstance = null;
    }

    // Graphology provides a easy to use implementation of Force Atlas 2 in a web worker
    const sensibleSettings = forceAtlas2.inferSettings(graph);
    
    // Use the inferred settings as base and customize for a more compact visualization
    const customSettings = {
      barnesHutOptimize: true,
      barnesHutTheta: 0.5,
      adjustSizes: false,
      iterationsPerRender: 15,
      settings: {
        // Start with sensible inferred settings
        ...sensibleSettings,
        // Override specific settings for more compact layout
        gravity: Math.max(sensibleSettings.gravity || 1, 8), // Use higher of inferred or our minimum
        strongGravityMode: true, // Use strong gravity for more compact layout
        scalingRatio: Math.min(sensibleSettings.scalingRatio || 1, 0.3), // Use lower scaling for compactness
        slowDown: Math.max(sensibleSettings.slowDown || 1, 3), // Slower movement for stability
        edgeWeightInfluence: Math.max(sensibleSettings.edgeWeightInfluence || 1, 3), // Stronger edge influence
        barnesHutTheta: 0.5
      }
    };

    fa2LayoutInstance = new FA2Layout(graph, customSettings);

    // Start the layout
    fa2LayoutInstance.start();

    // Return a promise that resolves when layout is complete or timeout
    return new Promise((resolve) => {
      // Set a timeout to stop the layout and resolve
      const timeoutId = setTimeout(() => {
        if (fa2LayoutInstance) {
          fa2LayoutInstance.stop();
        }
        // Apply constraints to prevent wild nodes after layout
        constrainNodePositions(graph);
        resolve(graph);
      }, 2000); // 2 second timeout for better convergence

      // Also listen for any potential stop events
      if (fa2LayoutInstance.on) {
        fa2LayoutInstance.on('stop', () => {
          clearTimeout(timeoutId);
          // Apply constraints to prevent wild nodes after layout
          constrainNodePositions(graph);
          resolve(graph);
        });
      }
    });
  } catch (error) {
    console.warn('Force Atlas 2 layout failed, falling back to hierarchical layout:', error);
    // Fallback to the original hierarchical positioning if FA2 fails
    return graph;
  }
};

/**
 * Clean up Force Atlas 2 layout instance
 */
export const cleanupFA2Layout = () => {
  if (fa2LayoutInstance) {
    fa2LayoutInstance.kill();
    fa2LayoutInstance = null;
  }
};

/**
 * Constrain node positions to prevent "wild" nodes from spreading too far
 * @param {Object} graph - Graphology graph instance
 */
const constrainNodePositions = (graph) => {
  const maxDistance = 1000; // Maximum distance from center (0,0)
  const pullStrength = 0.7; // How strongly to pull nodes back (0-1)
  
  graph.forEachNode((node, attributes) => {
    const x = attributes.x || 0;
    const y = attributes.y || 0;
    const distance = Math.sqrt(x * x + y * y);
    
    // If node is too far from center, pull it back
    if (distance > maxDistance) {
      const pullFactor = (maxDistance / distance) * pullStrength + (1 - pullStrength);
      graph.setNodeAttribute(node, 'x', x * pullFactor);
      graph.setNodeAttribute(node, 'y', y * pullFactor);
    }
  });
};

/**
 * Create a hash of graph data to detect changes
 * @param {Object} graphData - Object containing nodes and edges arrays
 * @returns {string} Hash string
 */
export const createGraphDataHash = (graphData) => {
  return JSON.stringify({ 
    nodeCount: graphData.nodes?.length, 
    edgeCount: graphData.edges?.length,
    firstNode: graphData.nodes?.[0]?.id 
  });
};

/**
 * Validate graph data structure
 * @param {Object} graphData - Object containing nodes and edges arrays
 * @returns {boolean} True if data is valid
 */
export const validateGraphData = (graphData) => {
  if (!graphData) return false;
  if (!Array.isArray(graphData.nodes)) return false;
  if (!Array.isArray(graphData.edges)) return false;
  
  // Check that all nodes have required properties
  for (const node of graphData.nodes) {
    if (!node.id) return false;
  }
  
  // Check that all edges reference valid nodes
  const nodeIds = new Set(graphData.nodes.map(n => n.id));
  for (const edge of graphData.edges) {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) return false;
  }
  
  return true;
};

/**
 * Get graph statistics
 * @param {Object} graph - Graphology graph instance
 * @returns {Object} Statistics object
 */
export const getGraphStats = (graph) => {
  return {
    nodeCount: graph.order,
    edgeCount: graph.size,
    density: graph.density(),
    connectedComponents: graph.connectedComponents ? graph.connectedComponents().length : 1
  };
};
